import { useState, useEffect, useCallback } from "react";
import axios from "../lib/api";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Check, Edit2, Plus, X, Lock, Globe, Trash2, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";

const ALL_INTEGRATIONS = [
  "prestashop", "shopify", "google_ads", "meta_ads", "google_analytics", "awin", "tiktok_ads"
];

const DEFAULT_FEATURES = {
  max_orders_per_year: 10000,
  max_users: 1,
  max_shops: 1,
  integrations_enabled: ["prestashop", "shopify"],
  advanced_product_analysis: false,
  advanced_financial_dashboard: false,
  csv_export: false,
  ai_insights: false,
  sync_frequency_minutes: 1440,
};

const EMPTY_NEW_PLAN = {
  plan_key: "",
  name: "",
  price: "",
  annual_price: "",
  is_private: false,
  allowed_org_ids: "",
  display_order: "",
  features: { ...DEFAULT_FEATURES }
};

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({});
  const [creating, setCreating] = useState(false);
  const [newPlan, setNewPlan] = useState(EMPTY_NEW_PLAN);
  const [saving, setSaving] = useState(false);
  const [expandedPlan, setExpandedPlan] = useState(null);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchPlans = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/plans`, { headers })
      .then(r => setPlans(r.data.plans))
      .catch(() => toast.error("Error al cargar los planes"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const startEdit = (plan) => {
    setEditing(plan.plan_key);
    setFormData({
      name: plan.name,
      price: plan.price,
      annual_price: plan.annual_price,
      is_private: plan.is_private || false,
      allowed_org_ids: (plan.allowed_org_ids || []).join(", "),
      display_order: plan.display_order,
      is_active: plan.is_active,
      features: { ...DEFAULT_FEATURES, ...plan.features }
    });
    setExpandedPlan(plan.plan_key);
  };

  const cancelEdit = () => { setEditing(null); setFormData({}); };

  const handleSave = async (plan_key) => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        annual_price: parseFloat(formData.annual_price),
        display_order: formData.display_order ? parseInt(formData.display_order) : undefined,
        allowed_org_ids: formData.allowed_org_ids
          ? formData.allowed_org_ids.split(",").map(s => s.trim()).filter(Boolean)
          : []
      };
      const res = await axios.patch(`${API}/admin/plans/${plan_key}`, payload, { headers });
      toast.success(res.data.stripe_synced
        ? `Plan actualizado y sincronizado con Stripe`
        : "Plan actualizado (sin cambios en Stripe)");
      fetchPlans();
      setEditing(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (plan_key) => {
    if (!window.confirm(`¿Desactivar el plan '${plan_key}'? Los usuarios actuales no se verán afectados.`)) return;
    try {
      await axios.delete(`${API}/admin/plans/${plan_key}`, { headers });
      toast.success("Plan desactivado");
      fetchPlans();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al desactivar");
    }
  };

  const handleCreate = async () => {
    if (!newPlan.plan_key || !newPlan.name || !newPlan.price || !newPlan.annual_price) {
      toast.error("plan_key, nombre, precio mensual y anual son obligatorios");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...newPlan,
        price: parseFloat(newPlan.price),
        annual_price: parseFloat(newPlan.annual_price),
        display_order: newPlan.display_order ? parseInt(newPlan.display_order) : undefined,
        allowed_org_ids: newPlan.allowed_org_ids
          ? newPlan.allowed_org_ids.split(",").map(s => s.trim()).filter(Boolean)
          : []
      };
      const res = await axios.post(`${API}/admin/plans`, payload, { headers });
      const synced = res.data.stripe_synced;
      toast.success(synced
        ? `Plan '${newPlan.name}' creado y sincronizado con Stripe`
        : `Plan creado (Stripe: ${res.data.stripe_error || "sin API key configurada"})`);
      fetchPlans();
      setCreating(false);
      setNewPlan(EMPTY_NEW_PLAN);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear el plan");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex justify-center py-20">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-plans">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Gestión de Planes</h1>
          <p className="text-sm text-slate-500 mt-0.5">Los cambios de precio crean nuevos precios en Stripe automáticamente</p>
        </div>
        <Button
          onClick={() => { setCreating(true); setExpandedPlan(null); setEditing(null); }}
          className="flex items-center gap-2"
          data-testid="create-plan-btn"
        >
          <Plus className="w-4 h-4" /> Nuevo Plan
        </Button>
      </div>

      {/* Create Plan Form */}
      {creating && (
        <Card className="border-2 border-blue-200 bg-blue-50/30" data-testid="create-plan-form">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base text-blue-800">Crear Nuevo Plan</CardTitle>
              <button onClick={() => setCreating(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-4 h-4" />
              </button>
            </div>
          </CardHeader>
          <CardContent>
            <PlanForm
              data={newPlan}
              onChange={setNewPlan}
              isNew
            />
            <div className="flex gap-2 mt-4 pt-4 border-t">
              <Button onClick={handleCreate} disabled={saving} className="gap-2" data-testid="confirm-create-plan-btn">
                {saving ? "Creando..." : "Crear y Sincronizar con Stripe"}
              </Button>
              <Button variant="outline" onClick={() => setCreating(false)}>Cancelar</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plans Grid */}
      <div className="grid md:grid-cols-3 gap-4">
        {plans.map((plan) => (
          <Card
            key={plan.plan_key}
            className={`transition-all ${!plan.is_active ? "opacity-50" : ""} ${editing === plan.plan_key ? "ring-2 ring-blue-400" : ""}`}
            data-testid={`admin-plan-${plan.plan_key}`}
          >
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base">{plan.name}</CardTitle>
                  {plan.is_private
                    ? <span className="flex items-center gap-1 px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium"><Lock className="w-3 h-3" />Privado</span>
                    : <span className="flex items-center gap-1 px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium"><Globe className="w-3 h-3" />Público</span>
                  }
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${plan.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                  {plan.is_active ? "Activo" : "Inactivo"}
                </span>
              </div>
            </CardHeader>

            <CardContent className="space-y-3 text-sm">
              {/* Read-only summary */}
              {editing !== plan.plan_key && (
                <>
                  <div className="space-y-1">
                    <Row label="Mensual" value={`${plan.price}€/mes`} />
                    <Row label="Anual" value={`${plan.annual_price}€/año`} />
                    <Row label="Max pedidos/año" value={plan.features?.max_orders_per_year?.toLocaleString("es-ES") || "-"} />
                    <Row label="Max usuarios" value={plan.features?.max_users || "-"} />
                    <Row label="Sync automático" value={
                      plan.features?.sync_frequency_minutes
                        ? { 30: "Cada 30 min", 60: "Cada hora", 240: "Cada 4h", 1440: "1x día" }[plan.features.sync_frequency_minutes] || `${plan.features.sync_frequency_minutes} min`
                        : "1x día"
                    } />
                  </div>

                  {/* Stripe IDs - collapsible */}
                  <button
                    className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 w-full"
                    onClick={() => setExpandedPlan(expandedPlan === plan.plan_key ? null : plan.plan_key)}
                  >
                    {expandedPlan === plan.plan_key ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    IDs de Stripe
                  </button>
                  {expandedPlan === plan.plan_key && (
                    <div className="space-y-1 bg-slate-50 rounded p-2">
                      <Row label="Producto" value={plan.stripe_product_id} mono />
                      <Row label="Precio mensual" value={plan.stripe_price_id} mono />
                      <Row label="Precio anual" value={plan.stripe_annual_price_id} mono />
                      {plan.stripe_product_id && (
                        <a
                          href={`https://dashboard.stripe.com/products/${plan.stripe_product_id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-1 text-xs text-blue-600 hover:underline mt-1"
                        >
                          <ExternalLink className="w-3 h-3" /> Ver en Stripe
                        </a>
                      )}
                    </div>
                  )}

                  {/* Features */}
                  <div className="flex flex-wrap gap-1 pt-1">
                    {plan.features?.csv_export && <Badge label="CSV Export" />}
                    {plan.features?.ai_insights && <Badge label="AI Insights" />}
                    {plan.features?.advanced_product_analysis && <Badge label="Análisis avanzado" />}
                    {plan.features?.advanced_financial_dashboard && <Badge label="Dashboard financiero" />}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-2 border-t border-slate-100">
                    <Button size="sm" variant="outline" className="flex-1 gap-1" onClick={() => startEdit(plan)} data-testid={`edit-plan-${plan.plan_key}`}>
                      <Edit2 className="w-3 h-3" /> Editar
                    </Button>
                    {plan.is_active && (
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeactivate(plan.plan_key)} data-testid={`deactivate-plan-${plan.plan_key}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </>
              )}

              {/* Edit Form */}
              {editing === plan.plan_key && (
                <>
                  <PlanForm data={formData} onChange={setFormData} />
                  <div className="flex gap-2 pt-3 border-t">
                    <Button size="sm" onClick={() => handleSave(plan.plan_key)} disabled={saving} className="flex-1" data-testid={`save-plan-${plan.plan_key}`}>
                      {saving ? "Guardando..." : "Guardar y Sincronizar"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={cancelEdit}>Cancelar</Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ── Plan Form (shared between edit and create) ─────────────────

function PlanForm({ data, onChange, isNew = false }) {
  const set = (key, val) => onChange(prev => ({ ...prev, [key]: val }));
  const setFeature = (key, val) => onChange(prev => ({
    ...prev,
    features: { ...prev.features, [key]: val }
  }));
  const toggleIntegration = (name) => {
    const current = data.features?.integrations_enabled || [];
    const updated = current.includes(name)
      ? current.filter(i => i !== name)
      : [...current, name];
    setFeature("integrations_enabled", updated);
  };

  return (
    <div className="space-y-4">
      {/* Basic info */}
      <div className="grid grid-cols-2 gap-3">
        {isNew && (
          <div className="col-span-2">
            <label className="block text-xs font-medium text-slate-600 mb-1">Plan Key <span className="text-red-500">*</span></label>
            <Input
              placeholder="ej: enterprise, custom_corp"
              value={data.plan_key || ""}
              onChange={e => set("plan_key", e.target.value.toLowerCase().replace(/\s+/g, "_"))}
              data-testid="new-plan-key"
              className="text-sm"
            />
            <p className="text-xs text-slate-400 mt-0.5">Solo minúsculas y guiones bajos. Identificador único.</p>
          </div>
        )}
        <div className="col-span-2">
          <label className="block text-xs font-medium text-slate-600 mb-1">Nombre <span className="text-red-500">*</span></label>
          <Input
            placeholder="ej: Enterprise, Custom Pro"
            value={data.name || ""}
            onChange={e => set("name", e.target.value)}
            data-testid="plan-name-input"
            className="text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Precio mensual (€) <span className="text-red-500">*</span></label>
          <Input
            type="number" min="0" step="0.01"
            placeholder="99.00"
            value={data.price || ""}
            onChange={e => set("price", e.target.value)}
            data-testid="plan-price-input"
            className="text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Precio anual (€) <span className="text-red-500">*</span></label>
          <Input
            type="number" min="0" step="0.01"
            placeholder="950.40"
            value={data.annual_price || ""}
            onChange={e => set("annual_price", e.target.value)}
            data-testid="plan-annual-price-input"
            className="text-sm"
          />
        </div>
      </div>

      {/* Limits */}
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Límites</p>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Max pedidos/año</label>
            <Input
              type="number" min="0"
              value={data.features?.max_orders_per_year || ""}
              onChange={e => setFeature("max_orders_per_year", parseInt(e.target.value) || 0)}
              data-testid="plan-max-orders"
              className="text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Max usuarios</label>
            <Input
              type="number" min="1"
              value={data.features?.max_users || ""}
              onChange={e => setFeature("max_users", parseInt(e.target.value) || 1)}
              data-testid="plan-max-users"
              className="text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Max tiendas</label>
            <Input
              type="number" min="1"
              value={data.features?.max_shops || ""}
              onChange={e => setFeature("max_shops", parseInt(e.target.value) || 1)}
              data-testid="plan-max-shops"
              className="text-sm"
            />
          </div>
        </div>
        <div className="mt-2">
          <label className="block text-xs text-slate-500 mb-1">Frecuencia de sync automático</label>
          <select
            value={data.features?.sync_frequency_minutes ?? 1440}
            onChange={e => setFeature("sync_frequency_minutes", parseInt(e.target.value))}
            className="w-full border border-slate-200 rounded-md text-sm px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
            data-testid="plan-sync-frequency"
          >
            <option value={1440}>1 vez al día (mínimo)</option>
            <option value={240}>Cada 4 horas</option>
            <option value={60}>Cada hora</option>
            <option value={30}>Cada 30 minutos (máximo)</option>
          </select>
          <p className="text-xs text-slate-400 mt-0.5">Frecuencia mínima permitida — el usuario elige dentro de este límite.</p>
        </div>
      </div>

      {/* Feature toggles */}
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Features</p>
        <div className="grid grid-cols-2 gap-y-1.5 gap-x-3">
          {[
            ["csv_export", "CSV Export"],
            ["ai_insights", "AI Insights"],
            ["advanced_product_analysis", "Análisis avanzado"],
            ["advanced_financial_dashboard", "Dashboard financiero"]
          ].map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!data.features?.[key]}
                onChange={e => setFeature(key, e.target.checked)}
                className="rounded"
              />
              <span className="text-xs text-slate-700">{label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Integrations */}
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Integraciones incluidas</p>
        <div className="flex flex-wrap gap-2">
          {ALL_INTEGRATIONS.map(name => {
            const active = (data.features?.integrations_enabled || []).includes(name);
            return (
              <button
                key={name}
                type="button"
                onClick={() => toggleIntegration(name)}
                className={`flex items-center gap-1 px-2 py-1 rounded text-xs border transition-colors ${
                  active
                    ? "bg-blue-600 border-blue-600 text-white"
                    : "border-slate-300 text-slate-500 hover:border-slate-400"
                }`}
              >
                {active && <Check className="w-3 h-3" />}
                {name.replace(/_/g, " ")}
              </button>
            );
          })}
        </div>
      </div>

      {/* Private plan */}
      <div className="pt-2 border-t border-slate-100">
        <label className="flex items-center gap-3 cursor-pointer">
          <div
            onClick={() => set("is_private", !data.is_private)}
            className={`relative w-10 h-5 rounded-full transition-colors ${data.is_private ? "bg-purple-500" : "bg-slate-200"}`}
          >
            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${data.is_private ? "translate-x-5" : "translate-x-0.5"}`} />
          </div>
          <div>
            <span className="text-sm font-medium text-slate-700">Plan Privado</span>
            <p className="text-xs text-slate-400">No aparece en la página pública de precios</p>
          </div>
          {data.is_private && <Lock className="w-4 h-4 text-purple-500 ml-auto" />}
        </label>

        {data.is_private && (
          <div className="mt-2">
            <label className="block text-xs text-slate-500 mb-1">
              Orgs permitidas (IDs separados por coma, vacío = cualquier admin puede asignarlo)
            </label>
            <Input
              placeholder="org_abc123, org_xyz456"
              value={data.allowed_org_ids || ""}
              onChange={e => set("allowed_org_ids", e.target.value)}
              className="text-xs"
            />
          </div>
        )}
      </div>

      {!isNew && (
        <div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={data.is_active !== false}
              onChange={e => set("is_active", e.target.checked)}
            />
            <span className="text-xs text-slate-700">Plan activo</span>
          </label>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, mono = false }) {
  return (
    <div className="flex justify-between text-xs gap-2">
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className={`text-slate-800 ${mono ? "font-mono text-[10px]" : ""} truncate max-w-[180px]`}>{value || "-"}</span>
    </div>
  );
}

function Badge({ label }) {
  return <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">{label}</span>;
}
